/**
 * A small block parser for the runbook, producing typed nodes rather than HTML.
 *
 * This is deliberately not a markdown library and deliberately not innerHTML.
 * The renderer walks these nodes and emits React elements, so there is no path
 * from document text to markup -- the same rule the rest of the app follows for
 * agent prose, kept here even though this content is checked into the repo.
 *
 * It supports exactly what the runbook uses. Anything it does not recognise
 * falls through as a paragraph, which is the right failure: the line still
 * reads, it just loses its formatting.
 */

export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'code'; text: string }
  // Emphasis nests: the runbook writes **`ops-dq-nightly` runs at 03:50 UTC**,
  // so a strong span holds spans rather than a string.
  | { kind: 'strong'; content: Inline[] }
  | { kind: 'em'; content: Inline[] }
  | { kind: 'link'; text: string; href: string }

export type Block =
  | { kind: 'heading'; level: 1 | 2 | 3; text: string; id: string }
  | { kind: 'paragraph'; content: Inline[] }
  | { kind: 'list'; ordered: boolean; items: Inline[][] }
  | { kind: 'checklist'; items: { id: string; content: Inline[] }[] }
  | { kind: 'code'; text: string }
  | { kind: 'table'; head: string[]; rows: Inline[][][] }
  | { kind: 'quote'; content: Inline[] }
  | { kind: 'rule' }

export interface Section {
  id: string
  title: string
  level: 2 | 3
}

export function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[`*_]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
}

/** Inline spans. Order matters: code first, so `**` inside a span stays literal. */
export function parseInline(text: string): Inline[] {
  const out: Inline[] = []
  // Order is the whole trick: code first so markers inside it stay literal,
  // then ** before *, or bold would parse as two empty emphases. Underscores
  // are deliberately not emphasis -- this document is full of snake_case
  // identifiers and `ns_subsidiary_id` is not an italic.
  const pattern =
    /`([^`]+)`|\*\*([^*]+)\*\*|\*([^*\n]+)\*|\[([^\]]+)\]\(([^)]+)\)|(https?:\/\/[^\s<>)\]]+)/g
  let last = 0
  let match: RegExpExecArray | null

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) out.push({ kind: 'text', text: text.slice(last, match.index) })
    if (match[1] !== undefined) out.push({ kind: 'code', text: match[1] })
    else if (match[2] !== undefined) out.push({ kind: 'strong', content: parseInline(match[2]) })
    else if (match[3] !== undefined) out.push({ kind: 'em', content: parseInline(match[3]) })
    else if (match[4] !== undefined && match[5] !== undefined) {
      out.push({ kind: 'link', text: match[4], href: match[5] })
    } else if (match[6] !== undefined) {
      // Trailing punctuation is sentence, not url.
      const raw = match[6].replace(/[.,;:]+$/, '')
      out.push({ kind: 'link', text: raw, href: raw })
      const trailing = match[6].slice(raw.length)
      if (trailing) out.push({ kind: 'text', text: trailing })
    }
    last = pattern.lastIndex
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) })
  return out.length > 0 ? out : [{ kind: 'text', text }]
}

/** Spans flattened to their readable text, emphasis included. */
export function plainText(spans: Inline[]): string {
  return spans
    .map((span) => (span.kind === 'strong' || span.kind === 'em' ? plainText(span.content) : span.text))
    .join('')
}

function splitRow(line: string): string[] {
  return line.replace(/^\||\|$/g, '').split('|').map((cell) => cell.trim())
}

export function parseMarkdown(source: string): { blocks: Block[]; sections: Section[] } {
  const lines = source.replace(/\r\n/g, '\n').split('\n')
  const blocks: Block[] = []
  const sections: Section[] = []
  const seen = new Map<string, number>()
  let i = 0

  /** Headings repeat across a long document; ids have to stay unique. */
  const uniqueId = (text: string): string => {
    const base = slug(text) || 'section'
    const count = seen.get(base) ?? 0
    seen.set(base, count + 1)
    return count === 0 ? base : `${base}-${count + 1}`
  }

  const flushParagraph = (buffer: string[]) => {
    if (buffer.length === 0) return
    blocks.push({ kind: 'paragraph', content: parseInline(buffer.join(' ').trim()) })
    buffer.length = 0
  }

  const paragraph: string[] = []

  while (i < lines.length) {
    const line = lines[i] ?? ''
    const trimmed = line.trim()

    if (trimmed === '') { flushParagraph(paragraph); i++; continue }

    if (/^(-{3,}|\*{3,})$/.test(trimmed)) {
      flushParagraph(paragraph)
      blocks.push({ kind: 'rule' })
      i++
      continue
    }

    const heading = /^(#{1,3})\s+(.*)$/.exec(trimmed)
    if (heading) {
      flushParagraph(paragraph)
      const level = heading[1]!.length as 1 | 2 | 3
      const text = heading[2]!.trim()
      const id = uniqueId(text)
      blocks.push({ kind: 'heading', level, text, id })
      // h1 is the document title, not a place to navigate to.
      if (level === 2 || level === 3) sections.push({ id, title: text, level })
      i++
      continue
    }

    // Indented code, which is how the runbook writes commands. Four spaces at
    // the top level, but seven where a command sits inside a numbered step, so
    // the block is detected at four-or-more and dedented by its own smallest
    // indent rather than by a fixed four.
    if (/^ {4,}\S/.test(line)) {
      flushParagraph(paragraph)
      const raw: string[] = []
      while (i < lines.length && (/^ {4,}/.test(lines[i] ?? '') || (lines[i] ?? '').trim() === '')) {
        // A trailing blank line belongs to the document, not the block.
        if ((lines[i] ?? '').trim() === '') {
          if (!/^ {4,}\S/.test(lines[i + 1] ?? '')) break
          raw.push('')
        } else {
          raw.push(lines[i] ?? '')
        }
        i++
      }
      const indents = raw.filter((l) => l.trim()).map((l) => (/^ */.exec(l)?.[0].length ?? 0))
      const dedent = indents.length > 0 ? Math.min(...indents) : 4
      blocks.push({
        kind: 'code',
        text: raw.map((l) => l.slice(dedent)).join('\n').replace(/\s+$/, ''),
      })
      continue
    }

    if (trimmed.startsWith('```')) {
      flushParagraph(paragraph)
      i++
      const body: string[] = []
      while (i < lines.length && !(lines[i] ?? '').trim().startsWith('```')) {
        body.push(lines[i] ?? '')
        i++
      }
      i++
      blocks.push({ kind: 'code', text: body.join('\n') })
      continue
    }

    if (trimmed.startsWith('|') && (lines[i + 1] ?? '').trim().startsWith('|')) {
      flushParagraph(paragraph)
      const head = splitRow(trimmed)
      i += 2 // header, then the --- separator
      const rows: Inline[][][] = []
      while (i < lines.length && (lines[i] ?? '').trim().startsWith('|')) {
        rows.push(splitRow((lines[i] ?? '').trim()).map(parseInline))
        i++
      }
      blocks.push({ kind: 'table', head, rows })
      continue
    }

    if (/^- \[[ x]\]\s/.test(trimmed)) {
      flushParagraph(paragraph)
      const items: { id: string; content: Inline[] }[] = []
      while (i < lines.length && /^- \[[ x]\]\s/.test((lines[i] ?? '').trim())) {
        let text = (lines[i] ?? '').trim().replace(/^- \[[ x]\]\s+/, '')
        i++
        // Continuation lines are indented under the item.
        while (i < lines.length && /^ {2,}\S/.test(lines[i] ?? '') && !/^ {4}\S/.test(lines[i] ?? '')) {
          text += ' ' + (lines[i] ?? '').trim()
          i++
        }
        items.push({ id: slug(text), content: parseInline(text) })
      }
      blocks.push({ kind: 'checklist', items })
      continue
    }

    const bullet = /^([-*])\s+(.*)$/.exec(trimmed)
    const numbered = /^(\d+)\.\s+(.*)$/.exec(trimmed)
    if (bullet || numbered) {
      flushParagraph(paragraph)
      const ordered = numbered !== null
      const items: Inline[][] = []
      while (i < lines.length) {
        const current = (lines[i] ?? '').trim()
        const match = ordered ? /^\d+\.\s+(.*)$/.exec(current) : /^[-*]\s+(.*)$/.exec(current)
        if (!match) break
        let text = match[1]!
        i++
        while (i < lines.length && /^ {2,}\S/.test(lines[i] ?? '') && !/^ {4}\S/.test(lines[i] ?? '')) {
          text += ' ' + (lines[i] ?? '').trim()
          i++
        }
        items.push(parseInline(text))
      }
      blocks.push({ kind: 'list', ordered, items })
      continue
    }

    if (trimmed.startsWith('>')) {
      flushParagraph(paragraph)
      const body: string[] = []
      while (i < lines.length && (lines[i] ?? '').trim().startsWith('>')) {
        body.push((lines[i] ?? '').trim().replace(/^>\s?/, ''))
        i++
      }
      blocks.push({ kind: 'quote', content: parseInline(body.join(' ')) })
      continue
    }

    paragraph.push(trimmed)
    i++
  }

  flushParagraph(paragraph)
  return { blocks, sections }
}

/** One top-level section of a document: its heading, what is under it, and the names of its subsections. */
export interface DocSection {
  id: string
  title: string
  /** Everything up to the next level-2 heading, less the heading itself and any closing rule. */
  blocks: Block[]
  /** Its level-3 headings' ids, so a link to one can open the section it sits in. */
  anchors: string[]
  /** Its level-3 headings, in order: the summary of what it contains. */
  subsections: string[]
}

/**
 * The document split at its level-2 headings, so each can open and close on its
 * own. `lead` is what comes before the first one -- the title and the note on
 * how to read it -- which is never closed.
 *
 * A rule that ends a section is the document separating sections; once each is
 * its own row it would be a second line under the first, so it is dropped here.
 * Nothing else is: every block lands in exactly one place.
 */
export function splitSections(blocks: ReadonlyArray<Block>): { lead: Block[]; sections: DocSection[] } {
  const lead: Block[] = []
  const sections: DocSection[] = []
  for (const block of blocks) {
    if (block.kind === 'heading' && block.level === 2) {
      sections.push({ id: block.id, title: block.text, blocks: [], anchors: [], subsections: [] })
      continue
    }
    const current = sections[sections.length - 1]
    if (!current) { lead.push(block); continue }
    current.blocks.push(block)
    if (block.kind === 'heading' && block.level === 3) {
      current.anchors.push(block.id)
      current.subsections.push(block.text)
    }
  }
  const trimRule = (list: Block[]) => { while (list[list.length - 1]?.kind === 'rule') list.pop() }
  trimRule(lead)
  for (const section of sections) trimRule(section.blocks)
  return { lead, sections }
}
